import type { Locale } from "date-fns";
import * as locales from "date-fns/locale";

type LanguageCode = "ja" | "en";
type CountryCode = "JP" | "US";

type CanonicalLocales = "ja-JP" | "en-US";
type DateFnsLocales = keyof typeof locales;

/**
 * Route locale string that is prefixed in the URL path (e.g., "en" or "jp").
 */
export const RoutePathLocale = {
  jp: "jp",
  en: "en",
} as const;

export type RouteLocaleValue =
  (typeof RoutePathLocale)[keyof typeof RoutePathLocale];

/**
 * A utility class to encapsulate locale-related information and transformations.
 * given a route locale (e.g., "en", "jp")
 */
export class RouteLocale {
  #canonical: CanonicalLocales;
  #routeLocale: RouteLocaleValue;
  constructor(routeLocale: RouteLocaleValue) {
    const routeLocaleToCanonicalLocale: Record<
      RouteLocaleValue,
      CanonicalLocales
    > = {
      jp: "ja-JP",
      en: "en-US",
    };

    this.#routeLocale = routeLocale;
    this.#canonical = routeLocaleToCanonicalLocale[routeLocale] ?? "en-US";
  }
  /**
   * Returns the first portion (language code) of the canonical locale (e.g., "ja" or "en").
   */
  get languageCode(): LanguageCode {
    return this.#canonical.split("-")[0] as LanguageCode;
  }
  /**
   * Returns the second portion (country code) of the canonical locale (e.g., "JP" or "US").
   */
  get countryCode(): CountryCode {
    return this.#canonical.split("-")[1] as CountryCode;
  }
  /**
   * Returns the key string used to import the corresponding locale from the `date-fns/locale` package.
   */
  get dateFnsLocaleKey(): DateFnsLocales {
    /**
     *  Maps canonical locale to it's corresponding date locale key strings that is used in the `date-fns/locale` package.
     */
    const canonicalLocaleToDateFnsLocaleKey: Record<
      CanonicalLocales,
      DateFnsLocales
    > = {
      "ja-JP": "ja",
      "en-US": "enUS",
    };

    return canonicalLocaleToDateFnsLocaleKey[this.#canonical] ?? "enUS";
  }
  /**
   * Returns the `date-fns/locale` object that corresponds to the route locale.
   */
  get dateFnsLocale(): Locale {
    // eslint-disable-next-line
    return locales[this.dateFnsLocaleKey] ?? locales.enUS;
  }
  /**
   * Returns the route locale string that is prefixed in the URL path (e.g., "en" or "jp").
   */
  get routeLocale(): RouteLocaleValue {
    return this.#routeLocale;
  }
  /**
   * Returns the full canonical locale string (e.g., "en-US" or "ja-JP")
   */
  get canonical(): CanonicalLocales {
    return this.#canonical;
  }
}

const japanLocale = new RouteLocale("jp");
const englishLocale = new RouteLocale("en");

export const localeMap: Record<
  CanonicalLocales | RouteLocaleValue | LanguageCode,
  RouteLocale
> = {
  jp: japanLocale,
  ja: japanLocale,
  "ja-JP": japanLocale,
  en: englishLocale,
  "en-US": englishLocale,
} as const;
